import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { FormError } from '@/core/FormError.atom';
import { ErrorMessageFormComponent } from '@/formbuilder/ErrorMessageFormComponent.atom';
import { FORM_ERROR } from '@/formbuilder/formBuilder.constants';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { validPassword } from '@/utilities/password.utilities';
import { Icon, TextField } from '@seeqdev/qomponents';
import type { FieldRenderProps } from 'react-final-form';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.constants';
import type { InputLengthStyleProps, TextFieldProps } from '@seeqdev/qomponents';

export interface PasswordCheckIF extends Omit<ValidatingFormComponent<string>, 'label'> {
  component: 'PasswordCheckFormComponent';
  size: string;
  placeholder?: string;
  type?: 'text' | 'password';
  maxLength?: number;
  max?: number;
  min?: number;
  as?: string;
  onBlur?: (value: string) => void;
  onFocus?: (value: string) => void;
  disabled?: boolean;
  fixedWidth?: boolean;
  skipMemo?: boolean;
  onIconClick?: () => any;
  key?: string;
}

export const PasswordCheckFormComponent = (props: PasswordCheckIF) => {
  const {
    name,
    extendValidation,
    validation,
    placeholder,
    onChange,
    onBlur,
    customErrorText,
    customErrorParams = {},
    type = 'text',
    fixedWidth = true,
    extraClassNames,
    tooltip,
    onIconClick,
  } = props;

  const { t } = useTranslation();
  const formState = useForm<{ newPassword?: string; confirmPassword?: string }>().getState();
  const formValues = formState.values;
  const valueMatch = formValues.newPassword === formValues.confirmPassword;
  const defaultValidation = (value: string) => !validPassword(value);

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  return (
    <Field name={name} validate={appliedValidation} key={name}>
      {({ input, meta }: FieldRenderProps<string, HTMLInputElement>) => {
        const properProps = _.assign({}, getFormFieldProps(formState, input, meta, props), {
          placeholder: placeholder && t(placeholder),
          value: input.value,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            input.onChange(e.target.value);
            onChange && onChange(e.target.value);
          },
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => _.isFunction(onBlur) && onBlur(e.target.value),
          onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.target.select(),
        });

        return (
          <div
            className={clsx('flexRowContainer', extraClassNames, {
              'width-100percent': fixedWidth,
            })}>
            <div className="flexColumnContainer flexAlignCenter">
              <PasswordCheckMemo
                value={properProps.value}
                onChange={properProps.onChange}
                placeholder={properProps.placeholder}
                onBlur={properProps.onBlur}
                onFocus={properProps.onFocus}
                size={properProps.size}
                type={type}
                testId={props.testId}
                showError={properProps.showError}
                extraClassNames={`${properProps.className} ${props.extraClassNames}`}
                maxLength={props.maxLength || props.max}
                minLength={props.min}
              />
              {tooltip && (
                <Icon
                  icon="fa-question-circle"
                  large={true}
                  tooltip={t(tooltip)}
                  extraClassNames="pl5"
                  onClick={onIconClick}
                />
              )}
            </div>
            {properProps.showError && customErrorText && (
              <FormError errorText={customErrorText} errorParameters={customErrorParams} />
            )}
            {!valueMatch && (
              <ErrorMessageFormComponent
                component="ErrorMessageFormComponent"
                failForm={true}
                type={FORM_ERROR}
                name="mismatchedPasswords"
                value="USER.PASSWORD_MISMATCH"
              />
            )}
          </div>
        );
      }}
    </Field>
  );
};

export const PasswordCheckMemo = React.memo<TextFieldProps & InputLengthStyleProps & { skipMemo?: boolean }>(
  TextField,
  (prev, next) =>
    !(
      !_.isEqual(prev.value, next.value) ||
      prev.readonly !== next.readonly ||
      prev.placeholder !== next.placeholder ||
      prev.extraClassNames !== next.extraClassNames ||
      prev.skipMemo
    ),
);
